<div class="container mx-auto">
  <!-- Section Header -->
  <div
    class="flex flex-wrap items-center justify-between mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-base-300"
  >
    <div class="flex-1">
      <h2
        class="text-xl sm:text-2xl font-bold text-base-content flex items-center gap-2"
      >
        <i class="fa-solid fa-users-rectangle text-primary"></i>
        <span>My Batches</span>
      </h2>
      <p class="text-xs sm:text-sm text-base-content/60 mt-1">
        Access courses and live classes assigned to your student cohort
      </p>
    </div>
  </div>

  @if (loading) {
    <div class="flex items-center justify-center py-20">
      <span
        class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary"
      ></span>
    </div>
  } @else if (batches.length === 0) {
    <div class="flex flex-col items-center justify-center py-16 text-center">
      <div
        class="w-20 h-20 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4"
      >
        <i
          class="fa-solid fa-users-rectangle text-3xl text-base-content/40"
        ></i>
      </div>
      <h3 class="text-xl font-bold text-base-content mb-2">
        No Batches Assigned
      </h3>
      <p class="text-sm text-base-content/60 max-w-md mx-auto mb-6">
        You haven't been assigned to any student cohorts or batches yet. Once
        your academy enrolls you into a batch, your cohort curriculum will
        appear here.
      </p>
      <div class="flex gap-3">
        <button
          class="daisy-btn daisy-btn-primary gap-2"
          routerLink="/dashboard/explore-classes"
        >
          <i class="fa-solid fa-compass"></i>
          Explore Classes
        </button>
      </div>
    </div>
  } @else {
    <!-- Batch Selector Tabs (if multiple) -->
    @if (batches.length > 1) {
      <div class="flex items-center gap-2 overflow-x-auto pb-4 mb-6">
        @for (batch of batches; track batch._id) {
          <button
            type="button"
            (click)="selectBatch(batch)"
            class="daisy-btn daisy-btn-sm shrink-0 gap-2"
            [ngClass]="
              selectedBatch?._id === batch._id
                ? 'daisy-btn-primary shadow-sm'
                : 'daisy-btn-outline'
            "
          >
            <i class="fa-solid fa-users-rectangle"></i>
            {{ batch.name }}
          </button>
        }
      </div>
    }

    @if (selectedBatch) {
      <div class="space-y-8">
        <!-- Selected Cohort Overview Banner (Matches Live Class Detail View Header) -->
        <div
          class="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 sm:p-8 text-white shadow-xl"
        >
          <div
            class="flex flex-col md:flex-row md:items-end justify-between gap-6"
          >
            <div>
              <div class="flex items-center gap-2 mb-2">
                <span
                  class="daisy-badge daisy-badge-sm font-semibold uppercase daisy-badge-success text-white"
                >
                  Active Cohort
                </span>
                @if (selectedBatch.startDate || selectedBatch.endDate) {
                  <span class="text-xs text-blue-100">
                    <i class="fa-regular fa-calendar mr-1"></i>
                    {{ formatDate(selectedBatch.startDate) }} -
                    {{ formatDate(selectedBatch.endDate) }}
                  </span>
                }
              </div>
              <h2 class="text-2xl sm:text-3xl font-bold mb-2">
                {{ selectedBatch.name }}
              </h2>
              @if (selectedBatch.description) {
                <p class="text-blue-100 text-sm sm:text-base max-w-2xl">
                  {{ selectedBatch.description }}
                </p>
              }
            </div>

            <!-- Quick Stats in Banner -->
            <div class="flex items-center gap-3 shrink-0">
              <div
                class="text-center px-4 py-2.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl"
              >
                <span class="block text-2xl font-black text-white">{{
                  selectedBatch.courses.length || 0
                }}</span>
                <span
                  class="text-[10px] uppercase font-bold text-blue-100 tracking-wider"
                  >Courses</span
                >
              </div>
              <div
                class="text-center px-4 py-2.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl"
              >
                <span class="block text-2xl font-black text-white">{{
                  selectedBatch.liveClasses.length || 0
                }}</span>
                <span
                  class="text-[10px] uppercase font-bold text-blue-100 tracking-wider"
                  >Live Classes</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Curriculum: Live Classes in Batch -->
        @if (selectedBatch.liveClasses.length) {
          <div class="space-y-4">
            <div>
              <h3
                class="text-xl font-bold text-base-content flex items-center gap-2"
              >
                <i class="fa-solid fa-chalkboard-user text-accent"></i>
                Batch Live Classes
                <span class="text-sm font-normal text-base-content/60"
                  >({{ selectedBatch.liveClasses.length }})</span
                >
              </h3>
              <p class="text-xs text-base-content/60 mt-0.5">
                Interactive live cohorts and lectures assigned to your batch
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              @for (lc of selectedBatch.liveClasses; track lc._id || lc.id) {
                <div
                  class="bg-base-100 border border-base-300 rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between group"
                >
                  <!-- Banner Image -->
                  <figure
                    class="relative aspect-video w-full m-0 bg-base-200 overflow-hidden"
                  >
                    @if (getBannerImage(lc)) {
                      <img
                        [src]="getBannerImage(lc)"
                        [alt]="lc.title"
                        class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    } @else {
                      <div
                        class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-base-content/50 bg-base-200"
                      >
                        <i class="fa-solid fa-chalkboard-user text-3xl"></i>
                        <span class="text-xs font-bold tracking-wider">{{
                          getInitials(lc.title)
                        }}</span>
                      </div>
                    }
                    @if (lc.subject) {
                      <span
                        class="absolute top-3 left-3 daisy-badge daisy-badge-accent font-bold text-[0.65rem] uppercase tracking-wider shadow-sm"
                      >
                        {{ lc.subject }}
                      </span>
                    }
                  </figure>
                  <div class="p-6">
                    <div class="flex items-start justify-between gap-2 mb-3">
                      <h4
                        class="font-bold text-lg text-base-content line-clamp-1"
                      >
                        {{ lc.title }}
                      </h4>
                      @if (isClassSubscribed(lc._id || lc.id)) {
                        <span
                          class="daisy-badge daisy-badge-success daisy-badge-sm font-semibold shrink-0 text-white"
                        >
                          <i
                            class="fa-solid fa-circle-check mr-1 text-[10px]"
                          ></i>
                          Subscribed
                        </span>
                      } @else {
                        <span
                          class="daisy-badge daisy-badge-ghost daisy-badge-sm font-semibold shrink-0"
                        >
                          Not Subscribed
                        </span>
                      }
                    </div>

                    @if (lc.details?.descriptionSummary || lc.description) {
                      <p
                        class="text-xs sm:text-sm text-base-content/70 line-clamp-2 mb-4"
                      >
                        {{ lc.details?.descriptionSummary || lc.description }}
                      </p>
                    }

                    <div
                      class="flex items-center gap-2 text-xs text-base-content/60 pt-3 border-t border-base-200"
                    >
                      <i class="fa-solid fa-language text-accent"></i>
                      <span>{{ lc.details?.medium || 'English Medium' }}</span>
                    </div>
                  </div>

                  <div class="p-4 bg-base-200/40 border-t border-base-300">
                    @if (isClassSubscribed(lc._id || lc.id)) {
                      <button
                        type="button"
                        (click)="goToLiveClass(lc)"
                        class="daisy-btn daisy-btn-accent daisy-btn-sm w-full gap-2 shadow text-white"
                      >
                        <i class="fa-solid fa-video"></i> Open Live Class
                      </button>
                    } @else {
                      <button
                        type="button"
                        (click)="goToLiveClass(lc)"
                        class="daisy-btn daisy-btn-outline daisy-btn-accent daisy-btn-sm w-full gap-2"
                      >
                        <i class="fa-solid fa-arrow-right"></i> Subscribe / View
                        Details
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Curriculum: Courses in Batch -->
        @if (selectedBatch.courses.length) {
          <div class="space-y-4">
            <div>
              <h3
                class="text-xl font-bold text-base-content flex items-center gap-2"
              >
                <i class="fa-solid fa-graduation-cap text-primary"></i>
                Batch Courses
                <span class="text-sm font-normal text-base-content/60"
                  >({{ selectedBatch.courses.length }})</span
                >
              </h3>
              <p class="text-xs text-base-content/60 mt-0.5">
                Asynchronous curriculum assigned to your batch
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              @for (course of selectedBatch.courses; track course._id) {
                <div
                  class="bg-base-100 border border-base-300 rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between group"
                >
                  <!-- Banner Image -->
                  <figure
                    class="relative aspect-video w-full m-0 bg-base-200 overflow-hidden"
                  >
                    @if (getBannerImage(course)) {
                      <img
                        [src]="getBannerImage(course)"
                        [alt]="course.title"
                        class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    } @else {
                      <div
                        class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-base-content/50 bg-base-200"
                      >
                        <i class="fa-solid fa-graduation-cap text-3xl"></i>
                        <span class="text-xs font-bold tracking-wider">{{
                          getInitials(course.title)
                        }}</span>
                      </div>
                    }
                    @if (course.subject) {
                      <span
                        class="absolute top-3 left-3 daisy-badge daisy-badge-primary font-bold text-[0.65rem] uppercase tracking-wider shadow-sm"
                      >
                        {{ course.subject }}
                      </span>
                    }
                  </figure>
                  <div class="p-6">
                    <div class="flex items-start justify-between gap-2 mb-3">
                      <h4
                        class="font-bold text-lg text-base-content line-clamp-1"
                      >
                        {{ course.title }}
                      </h4>
                      @if (isCourseEnrolled(course._id)) {
                        <span
                          class="daisy-badge daisy-badge-success daisy-badge-sm font-semibold shrink-0 text-white"
                        >
                          <i
                            class="fa-solid fa-circle-check mr-1 text-[10px]"
                          ></i>
                          Enrolled
                        </span>
                      } @else {
                        <span
                          class="daisy-badge daisy-badge-ghost daisy-badge-sm font-semibold shrink-0"
                        >
                          Not Enrolled
                        </span>
                      }
                    </div>

                    @if (
                      course.details?.descriptionSummary || course.description
                    ) {
                      <p
                        class="text-xs sm:text-sm text-base-content/70 line-clamp-2 mb-4"
                      >
                        {{
                          course.details?.descriptionSummary ||
                            course.description
                        }}
                      </p>
                    }

                    <div
                      class="flex items-center gap-2 text-xs text-base-content/60 pt-3 border-t border-base-200"
                    >
                      <i class="fa-solid fa-tag text-primary"></i>
                      <span class="font-semibold">{{
                        course.settings?.price
                          ? 'LKR ' + course.settings.price
                          : 'Free'
                      }}</span>
                    </div>
                  </div>

                  <div class="p-4 bg-base-200/40 border-t border-base-300">
                    @if (isCourseEnrolled(course._id)) {
                      <button
                        type="button"
                        (click)="goToCourse(course)"
                        class="daisy-btn daisy-btn-primary daisy-btn-sm w-full gap-2 shadow"
                      >
                        <i class="fa-solid fa-play"></i> Continue Learning
                      </button>
                    } @else {
                      <button
                        type="button"
                        (click)="goToCourse(course)"
                        class="daisy-btn daisy-btn-outline daisy-btn-primary daisy-btn-sm w-full gap-2"
                      >
                        <i class="fa-solid fa-cart-shopping"></i> Enroll / View
                        Details
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Empty Curriculum State if batch has neither courses nor live classes -->
        @if (
          !selectedBatch.courses.length && !selectedBatch.liveClasses.length
        ) {
          <div
            class="bg-base-200/40 border border-dashed border-base-300 rounded-2xl p-12 text-center text-sm text-base-content/60"
          >
            <i class="fa-solid fa-book-open text-3xl mb-3 opacity-50 block"></i>
            <p class="font-bold text-base-content text-base mb-1">
              No Curriculum Assigned Yet
            </p>
            <p>
              Your academy has not attached any courses or live classes to this
              batch yet.
            </p>
          </div>
        }
      </div>
    }
  }
</div>
